/**
 * Dynamic list of label page ranges (+ / −). Stateless — parent holds ranges and wires save/close.
 */
import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { LabelRangeDraft } from './types';
import LabelRangeRow from './LabelRangeRow';
import styles from './labelSeparation.styles';

type LabelRangeFormProps = {
  ranges: LabelRangeDraft[];
  saving?: boolean;
  disabled?: boolean;
  onAddRange: () => void;
  /* eslint-disable no-unused-vars -- type-only callback signatures */
  onRemoveRange: (id: string) => void;
  onRangeChange: (id: string, field: keyof Omit<LabelRangeDraft, 'id'>, value: string) => void;
  /* eslint-enable no-unused-vars */
  onSave: () => void;
  onClose: () => void;
};

const LabelRangeForm = ({
  ranges,
  saving = false,
  disabled = false,
  onAddRange,
  onRemoveRange,
  onRangeChange,
  onSave,
  onClose
}: LabelRangeFormProps) => (
  <Box component='form' onSubmit={event => event.preventDefault()} sx={styles.rangeForm}>
    {ranges.map((range, index) => (
      <LabelRangeRow
        key={range.id}
        index={index}
        range={range}
        canRemove={ranges.length > 1}
        disabled={disabled || saving}
        onFieldChange={(field, value) => onRangeChange(range.id, field, value)}
        onRemove={() => onRemoveRange(range.id)}
      />
    ))}

    <IconButton
      aria-label='Add label range'
      color='primary'
      disabled={disabled || saving}
      onClick={onAddRange}>
      <AddIcon />
    </IconButton>

    <Stack direction='row' spacing={2} sx={styles.formActions}>
      <Button
        type='button'
        variant='contained'
        color='primary'
        disabled={disabled || saving}
        sx={styles.actionButton}
        onClick={onSave}>
        <Typography>{saving ? 'Saving…' : 'Save'}</Typography>
      </Button>
      <Button
        type='button'
        variant='outlined'
        disabled={saving}
        sx={styles.actionButton}
        onClick={onClose}>
        <Typography>Close</Typography>
      </Button>
    </Stack>
  </Box>
);

LabelRangeForm.defaultProps = {
  saving: false,
  disabled: false
};

export default LabelRangeForm;
